import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import QRCode from 'react-qr-code';
import { themeOptions } from '../general/ThemeSideMenu';
import { useCompactPortrait, useNavigation } from './NavigationContext';
import './BottomNavigation.css';

function OrientationGuidance({ preferredOrientation, preferredLayout }) {
  const [dismissed, setDismissed] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setDismissed(false), [pathname, preferredOrientation, preferredLayout]);
  if (dismissed || (preferredOrientation !== 'landscape' && preferredLayout !== 'wide')) return null;
  return (
    <aside className="orientation-guidance" role="status">
      <i className="bi bi-phone-landscape" aria-hidden="true" />
      <span>This page works best in landscape. Try rotating your device.</span>
      <button type="button" aria-label="Dismiss orientation guidance" onClick={() => setDismissed(true)}>
        <i className="bi bi-x-lg" aria-hidden="true" />
      </button>
    </aside>
  );
}

export default function BottomNavigation({ theme = 'system', setTheme }) {
  const compact = useCompactPortrait();
  const { actions, preferences } = useNavigation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [moreView, setMoreView] = useState('actions');
  const { pathname, search } = useLocation();
  const primary = actions.participation || actions.primary;
  let detailKey = moreView;
  const detailStack = [];
  while (actions[`modal-${detailKey}`]?.parent === detailKey) {
    detailKey = `modal-${detailKey}`;
    detailStack.push(actions[detailKey]);
  }
  const detail = detailStack[detailStack.length - 1];
  const detailParentLabel = detailStack.length > 1 ? detailStack[detailStack.length - 2].label : actions[moreView]?.label;
  const activeRoot = Object.values(actions).find(action => action.parent && !actions[action.parent]?.parent)?.parent;
  useEffect(() => {
    if (compact && activeRoot) {
      setMoreView(activeRoot);
      setMoreOpen(true);
    }
  }, [compact, activeRoot]);
  const goBack = () => detail ? detail.onClick() : setMoreView('actions');
  const closeMore = () => {
    [...detailStack].reverse().forEach(action => action.onClick());
    setMoreOpen(false);
  };
  const secondaryActions = Object.entries(actions)
    .filter(([key, action]) => key !== 'menu' && key !== 'primary' && key !== 'theme' && key !== 'participation' && !action.parent)
    .sort(([left], [right]) => Number(left === 'about') - Number(right === 'about'));

  useEffect(() => {
    setMoreOpen(false);
    setMoreView('actions');
  }, [pathname, search, compact]);
  useEffect(() => {
    document.body.classList.toggle('compact-navigation-active', compact);
    return () => document.body.classList.remove('compact-navigation-active');
  }, [compact]);

  if (!compact) return null;

  return (
    <>
      <OrientationGuidance {...preferences} />
      <nav className="app-bottom-navigation" aria-label="Page actions">
        <button type="button" disabled={!actions.menu} onClick={() => {
          if (actions.menu.renderContent) {
            setMoreView('menu');
            setMoreOpen(true);
          } else {
            actions.menu.onClick();
          }
        }}>
          <i className="bi bi-list" aria-hidden="true" /><span>Menu</span>
        </button>
        <button type="button" className="app-bottom-primary" disabled={!primary || primary.disabled} onClick={() => {
          if (actions.participation) {
            setMoreView('participation');
            setMoreOpen(true);
          } else {
            primary.onClick();
          }
        }}>
          <i className={`bi ${primary?.icon || 'bi-lightning'}`} aria-hidden="true" />
          <span>{primary?.label || 'No Page Actions'}</span>
        </button>
        <button type="button" aria-haspopup="dialog" aria-expanded={moreOpen} onClick={() => { setMoreView('actions'); setMoreOpen(true); }}>
          <i className="bi bi-three-dots" aria-hidden="true" /><span>More</span>
        </button>
      </nav>
      <Modal show={moreOpen} onHide={closeMore} enforceFocus={false} centered className={`mobile-more-modal${moreView === 'menu' ? ' mobile-navigation-modal' : ''}`}>
        <Modal.Header>
          {moreView !== 'actions' && (
            <button type="button" className="mobile-more-back" aria-label={detail ? `Back to ${detailParentLabel}` : 'Back to More'} onClick={goBack}>
              <i className="bi bi-arrow-left" aria-hidden="true" />
            </button>
          )}
          <Modal.Title>
            {moreView === 'actions' ? 'More' : (
              <button type="button" className="mobile-more-title-back" onClick={goBack}>
                {detail ? detail.label : moreView === 'theme' ? 'Theme Selector' : actions[moreView]?.label}
              </button>
            )}
          </Modal.Title>
          <button type="button" className="mobile-more-close" aria-label="Close" onClick={closeMore}>
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </Modal.Header>
        <Modal.Body>
          {detail ? (
            <div className="mobile-more-view">
              {detailStack.map((action, index) => (
                <div key={action.parent} hidden={index !== detailStack.length - 1}>{action.renderContent()}</div>
              ))}
            </div>
          ) : moreView === 'theme' ? (
            <div key="theme" className="mobile-more-view mobile-theme-view">
              <div role="group" aria-label="Theme options" className="mobile-more-actions">
                {themeOptions.map(option => (
                  <button type="button" key={option.name} aria-pressed={theme === option.name} onClick={() => setTheme(option.name)}>
                    {option.icon}<span>{option.label}</span>
                    {theme === option.name && <i className="bi bi-check-lg" aria-hidden="true" />}
                  </button>
                ))}
              </div>
            </div>
          ) : actions[moreView]?.renderContent ? (
            <div key={moreView} className="mobile-more-view">
              {actions[moreView].renderContent({ closeMore: () => setMoreOpen(false) })}
            </div>
          ) : (
          <div key="actions" className="mobile-more-view mobile-actions-view">
          <div className="mobile-more-actions">
            <button type="button" onClick={() => setMoreView('theme')}>
              <i className="bi bi-mask" aria-hidden="true" />
              <span>Theme Selector</span><i className="bi bi-chevron-right" aria-hidden="true" />
            </button>
            {secondaryActions.map(([key, action]) => (
              <button type="button" key={key} disabled={action.disabled} onClick={() => {
                if (action.renderContent) {
                  setMoreView(key);
                  return;
                }
                setMoreOpen(false);
                action.onClick();
              }}>
                <i className={`bi ${action.icon || 'bi-gear'}`} aria-hidden="true" />
                <span>{action.label}</span><i className="bi bi-chevron-right" aria-hidden="true" />
              </button>
            ))}
          </div>
          <div className="mobile-more-qr">
            <QRCode title="Share this page via QR Code" value={window.location.href} size={120} bgColor="#fff" fgColor="#000" />
            <span>Share this page</span>
          </div>
          </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
